import * as React from "react";
import cn from "classnames";
import styles from "./styles.module.scss";

interface ITabsContext {
    index?: number;
    onSelectTab?: (index: number) => void;
}

interface ITabsProps {
    children?: React.ReactNode;
    index?: number;
    onChange?: (index: number) => void;
}

interface ITabListProps {
    children?: React.ReactNode;
    className?: string;
}

interface ITabProps {
    children?: React.ReactNode;
    index?: number;
    className?: string;
}

interface ITabPanelProps {
    children?: React.ReactNode;
    index?: number;
    className?: string;
}

interface ITabPanelsProps {
    children?: React.ReactNode;
    className?: string;
}

const TabsContext = React.createContext<Partial<ITabsContext>>({
    index: undefined,
});

export function Tabs({ children, index, onChange }: ITabsProps): JSX.Element {
    const [selectedIndex, setSelectedIndex] = React.useState(index ?? 0);

    const onSelectTab = (index: number) => {
        onChange?.(index);
        setSelectedIndex(index);
    };

    React.useEffect(() => {
        if (!index) {
            return;
        }

        setSelectedIndex(index);
    }, [index]);

    return (
        <TabsContext.Provider value={{ index: selectedIndex, onSelectTab }}>
            {children}
        </TabsContext.Provider>
    );
}

export function TabList({ children, className }: ITabListProps): JSX.Element {
    return (
        <div
            role="tablist"
            aria-orientation="horizontal"
            className={cn(styles.tabList, className)}
        >
            {React.Children.map(children, (child, index) => {
                if (!React.isValidElement(child)) {
                    return null;
                }
                return React.cloneElement(child, { index });
            })}
        </div>
    );
}

export function Tab({ index, children, className }: ITabProps): JSX.Element {
    const { index: selectedIndex, onSelectTab } = React.useContext(TabsContext);

    const isSelected = selectedIndex === index;

    return (
        <button
            role="tab"
            tabIndex={isSelected ? 0 : 1}
            aria-selected={isSelected}
            onClick={() => index !== undefined && onSelectTab?.(index)}
            className={cn(
                styles.tab,
                isSelected && styles.tabSelected,
                className,
            )}
            type="button"
        >
            {children}
        </button>
    );
}

export function TabPanels({
    children,
    className,
}: ITabPanelsProps): JSX.Element {
    return (
        <div className={className}>
            {React.Children.map(children, (child, index) => {
                if (!React.isValidElement(child)) {
                    return null;
                }
                return React.cloneElement(child, { index });
            })}
        </div>
    );
}

export function TabPanel({
    index,
    children,
    className,
}: ITabPanelProps): JSX.Element {
    const { index: selectedIndex } = React.useContext(TabsContext);

    const isSelected = selectedIndex === index;
    const hidden = !isSelected;

    return (
        <div role="tabpanel" hidden={hidden} className={className}>
            {children}
        </div>
    );
}
